import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { DeleteTeamDirective } from '@app/+authenticated/shared/directives/delete-team.directive';
import { PermissionDirective } from '@app/+authenticated/shared/permission.directive';
import { getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { DepartmentService, getAllDepartmentsGroupedByLocation } from '@app/reducers/orm/department/department.service';
import { getActiveEmployees } from '@app/reducers/orm/employee/employee.selectors';
import { getLocations, LocationService } from '@app/reducers/orm/location/location.service';
import { TeamType } from '@app/reducers/orm/team/team.model';
import { getActiveTeamsGroupedByDepartment } from '@app/reducers/orm/team/team.service';
import { Store } from '@ngrx/store';
import { _, TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconAddRegular } from '@sb/mgc-icons/mgcIcon-add_regular.icon';
import { mgcIconBuilding1CuteRe } from '@sb/mgc-icons/mgcIcon-building_1_cute_re.icon';
import { mgcIconCheckRegular } from '@sb/mgc-icons/mgcIcon-check_regular.icon';
import { mgcIconDelete2Regular } from '@sb/mgc-icons/mgcIcon-delete_2_regular.icon';
import { mgcIconEyeCloseRegular } from '@sb/mgc-icons/mgcIcon-eye_close_regular.icon';
import { mgcIconFilter2Regular } from '@sb/mgc-icons/mgcIcon-filter_2_regular.icon';
import { mgcIconGroup2Regular } from '@sb/mgc-icons/mgcIcon-group_2_regular.icon';
import { mgcIconGroup3Regular } from '@sb/mgc-icons/mgcIcon-group_3_regular.icon';
import { mgcIconPencilRegular } from '@sb/mgc-icons/mgcIcon-pencil_regular.icon';
import { mgcIconRefresh3Regular } from '@sb/mgc-icons/mgcIcon-refresh_3_regular.icon';
import { mgcIconSearchRegular } from '@sb/mgc-icons/mgcIcon-search_regular.icon';
import { hasPermission } from '@sb/shared/permission/util';
import { TooltipDirective } from '@sb/tooltip';
import { BadgeComponent } from '@sb/ui/badge';
import { ButtonComponent, IconButtonComponent } from '@sb/ui/button';
import { CardModule } from '@sb/ui/card';
import { EmptyStateComponent } from '@sb/ui/feedback';
import { IconComponent, IconRegistry, type Icon } from '@sb/ui/icon';
import { MenuComponent, MenuItemCheckboxComponent, MenuItemComponent, MenuTriggerDirective } from '@sb/ui/menu';
import { SearchInputComponent } from '@sb/ui/search-input';
import { SpinnerComponent } from '@sb/ui/spinner';
import { hasReachedQuota, SubscriptionQuotaDirective } from '@shared/subscription-plan/subscription-quota.directive';
import { PlanType, QuotaType } from '@shiftbase-com/ts-shared/models';
import { EMPTY, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { TurnoverService } from '../../insights/turnover/services/turnover.service';
import {
  buildOrganizationRows,
  computeLocationsWithEmployees,
  countActiveDepartments,
  type OrganizationStatus,
} from './organization-rows';

interface CreateMenuItem {
  label: string;
  icon: Icon;
  command: () => void;
}

@Component({
  selector: 'locations-table',
  imports: [
    TranslatePipe,
    IconComponent,
    IconButtonComponent,
    PermissionDirective,
    SubscriptionQuotaDirective,
    ButtonComponent,
    CardModule,
    EmptyStateComponent,
    BadgeComponent,
    RouterModule,
    MenuComponent,
    MenuItemComponent,
    MenuItemCheckboxComponent,
    MenuTriggerDirective,
    SearchInputComponent,
    SpinnerComponent,
    TooltipDirective,
    DeleteTeamDirective,
  ],
  templateUrl: './locations-table.component.html',
})
export class LocationsTableComponent {
  #store = inject<Store<AppState>>(Store);
  #locationService = inject(LocationService);
  #departmentService = inject(DepartmentService);
  #router = inject(Router);
  #turnoverService = inject(TurnoverService);
  #destroyRef = inject(DestroyRef);

  #allLocations = this.#store.selectSignal(getLocations);
  #allDepartments = this.#store.selectSignal(getAllDepartmentsGroupedByLocation);
  #allTeams = this.#store.selectSignal(getActiveTeamsGroupedByDepartment);
  #activeEmployees = this.#store.selectSignal(getActiveEmployees);
  #subscription = this.#store.selectSignal(getAccountSubscription);
  #permissionState = this.#store.selectSignal(getPermissionState);

  quotaType = QuotaType;
  TeamType = TeamType;

  searchQuery = signal('');
  showActive = signal(true);
  showInactive = signal(false);

  #statuses = computed<ReadonlySet<OrganizationStatus>>(() => {
    const statuses = new Set<OrganizationStatus>();

    if (this.showActive()) {
      statuses.add('active');
    }

    if (this.showInactive()) {
      statuses.add('inactive');
    }

    return statuses;
  });

  visibleLocations = computed(() =>
    buildOrganizationRows({
      locations: this.#allLocations(),
      departmentsByLocation: this.#allDepartments(),
      teamsByDepartment: this.#allTeams(),
      query: this.searchQuery(),
      statuses: this.#statuses(),
    }),
  );

  departmentsCount = computed(() => countActiveDepartments(this.#allLocations(), this.#allDepartments()));

  locationsWithEmployees = computed(() =>
    computeLocationsWithEmployees(this.#activeEmployees(), this.#allDepartments(), this.#allTeams()),
  );

  isFreePlan = computed(() => this.#subscription()?.plan.type === PlanType.FREE);

  hasReachedTeamQuota = computed(() => {
    const subscription = this.#subscription();

    return !!subscription && hasReachedQuota(QuotaType.TEAMS, subscription);
  });

  #hasReachedDepartmentQuota = computed(() => {
    const subscription = this.#subscription();

    return !!subscription && hasReachedQuota(QuotaType.DEPARTMENTS, subscription) && !this.isFreePlan();
  });

  #canCreateTeams = computed(() =>
    hasPermission({ userId: 'me', departments: 'any', permissions: ['Create teams'] }, this.#permissionState()),
  );

  menuItems = computed<CreateMenuItem[]>(() => {
    const items: CreateMenuItem[] = [];
    const isFreePlan = this.isFreePlan();

    if (!isFreePlan) {
      items.push({
        label: _('Create location'),
        icon: 'building-1-cute-re',
        command: () => this.#router.navigate(['', { outlets: { modal: ['location-group'] } }]),
      });

      if (!this.#hasReachedDepartmentQuota()) {
        items.push({
          label: _('Create department'),
          icon: 'group-3-regular',
          command: () => this.#router.navigate(['', { outlets: { modal: ['location'] } }]),
        });
      }
    }

    if ((this.#canCreateTeams() || isFreePlan) && !this.hasReachedTeamQuota()) {
      items.push({
        label: _('Create team'),
        icon: 'group-2-regular',
        command: () => this.#router.navigate(['', { outlets: { modal: ['team'] } }]),
      });
    }

    return items;
  });

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconAddRegular,
      mgcIconBuilding1CuteRe,
      mgcIconCheckRegular,
      mgcIconDelete2Regular,
      mgcIconEyeCloseRegular,
      mgcIconFilter2Regular,
      mgcIconGroup2Regular,
      mgcIconGroup3Regular,
      mgcIconPencilRegular,
      mgcIconRefresh3Regular,
      mgcIconSearchRegular,
    ]);
  }

  deactivateLocationGroup(id: string): void {
    this.#locationService.deactivate(id).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }

  activateLocationGroup(id: string): void {
    this.#locationService.activate(id).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }

  deactivateDepartment(id: string): void {
    this.#turnoverService
      .showDeactivatedAllocationReviewDialogIfNeeded({ entityType: 'department', entityId: id })
      .pipe(
        switchMap(() => this.#departmentService.validateDeactivate(id)),
        catchError((error) => {
          if (error.status === 422) {
            const hasValidationErrorsForTargetTeamId = 'targetTeamId' in error.error.meta.validation_errors;
            const hasValidationErrorsForTargetDepartmentId = 'targetDepartmentId' in error.error.meta.validation_errors;

            this.#departmentService.deactivateFields.hasTargetDepartmentId = hasValidationErrorsForTargetDepartmentId;
            this.#departmentService.deactivateFields.hasTargetTeamId = hasValidationErrorsForTargetTeamId;

            if (hasValidationErrorsForTargetTeamId || hasValidationErrorsForTargetDepartmentId) {
              this.triggerDepartmentDeletionFlow(id);
            }
          }
          return EMPTY;
        }),
        switchMap(() => of(id)),
        switchMap((departmentId) => {
          if (departmentId) {
            return this.#departmentService.deactivate(departmentId);
          }
          return of(null);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  triggerDepartmentDeletionFlow(departmentId: string): void {
    this.#router.navigate(['', { outlets: { modal: ['deactivate-department', departmentId] } }]);
  }

  activateDepartment(id: string): void {
    this.#departmentService.activate(id).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }
}
